import { Box } from "@mui/material";
import {
  DataGrid,
  DataGridProps,
  gridClasses,
  GridColDef,
  GridNoRowsOverlay,
} from "@mui/x-data-grid";
import WorkflowTableTextCell from "../cells/WorkflowTableTextCell";
import WorkflowAssetTableRiskScoreCell from "./components/WorkflowAssetTableRiskScoreCell";
import AssetCell from "./components/WorkflowAssetTableAssetCell";
import WorkflowAssetTableRiskFactorsCell from "./components/WorkflowAssetTableRiskFactorsCell";
import { FormatRelativeDate } from "~/lib/date";

export default function WorkflowAssetsTable({
  ...props
}: Omit<DataGridProps, "columns">) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <DataGrid
        columnHeaderHeight={48}
        rowHeight={80}
        disableRowSelectionOnClick
        disableColumnMenu
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        slots={{ noRowsOverlay: NoRowsOverlay }}
        slotProps={{
          root: {
            "data-testid": "workflow-assets-table",
          },
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
        }}
        sx={{
          borderRadius: 2,
          border: "none",
          backgroundColor: "background.paper",
          [`& .${gridClasses.columnSeparator}`]: {
            [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
              display: "none",
            },
          },
          [`& .${gridClasses.columnHeader}`]: {
            ...tableHeaderStyles,
            backgroundColor: "background.dark",
            borderBottom: "none !important",
          },
          [`& .${gridClasses.sortIcon}`]: {
            fontSize: "10px",
            color: "#0A0A22",
          },
          [`& .${gridClasses.footerContainer}`]: {
            backgroundColor: "background.dark",
            borderTop: "none !important",
            backgroundImage: "none",
            opacity: 1,
          },
          // remove focus outline
          [`& .${gridClasses.columnHeader}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.columnHeader}:focus-within`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus-within`]: {
            outline: "none",
          },
          "& .MuiDataGrid-row--borderBottom": {
            borderBottom: "none",
          },
          "& .left-cell": {
            pl: 3,
          },
        }}
        paginationMode="server"
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{
          sorting: { sortModel: [{ field: "risk_score", sort: "desc" }] },
        }}
        {...props}
        columns={columns}
      />
    </Box>
  );
}

export const tableTextStyles = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "12px",
  fontWeight: 500,
  fontStyle: "normal",
  lineHeight: "1.33", // 16px / 12px → 1.33
};

const tableHeaderStyles = {
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "normal",
  color: "text.secondary",
};

function NoRowsOverlay() {
  return (
    <GridNoRowsOverlay
      sx={{
        backgroundColor: "background.paper",
      }}
    />
  );
}

const columns: GridColDef[] = [
  {
    field: "risk_score",
    headerName: "Risk Score",
    headerClassName: "left-cell",
    cellClassName: "left-cell",
    width: 126,
    resizable: false,
    headerAlign: "left",
    align: "left",
    renderCell: (params) => (
      <WorkflowAssetTableRiskScoreCell value={params.value} />
    ),
  },
  // We don't have logos for assets yet
  // {
  //   field: "logo",
  //   headerName: "",
  //   width: 48,
  //   resizable: false,
  //   sortable: false,
  //   filterable: false,
  //   renderCell: (params) => <LogoCell value={params.value} />,
  // },
  {
    field: "asset",
    headerName: "Asset",
    minWidth: 300,
    flex: 1,
    resizable: false,
    renderCell: (params) => (
      <AssetCell value={params.value} url={params.row.url} />
    ),
  },
  {
    field: "risk_factors",
    headerName: "Risk Factors",
    width: 160,
    resizable: false,
    renderCell: (params) => (
      <WorkflowAssetTableRiskFactorsCell value={params.value} />
    ),
  },
  {
    field: "lastUpdated",
    headerName: "Last updated",
    headerClassName: "right-cell",
    cellClassName: "right-cell",
    width: 136,
    resizable: false,
    renderCell: (params) => (
      <WorkflowTableTextCell text={FormatRelativeDate(params.value)} />
    ),
  },
];
